import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

function CreditsLegal() {
    return (
        <div className="legal-page">
            <header className="page-section-head">
                <h1>Legal <span className="page-section-current">Credits & Legal</span></h1>
            </header>
            <div className="legal-container">
                <p className="legal-updated">Last Updated: July 12, 2026</p>

                <section>
                    <p>
                        Trade Spot is a fan-made platform for trading collectible card games. 
                        We use data and images from various sources, each with their own licenses and policies. 
                        This page outlines our compliance with those policies and provides proper attribution.
                    </p>
                    <p>
                        This page is part of our legal documentation. Please also review 
                        our <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.
                    </p>
                </section>

                {/* ==================== GAME-SPECIFIC DISCLAIMERS ==================== */}
                
                <section>
                    <h2>Game Content Disclaimers</h2>
                    
                    {/* Magic: The Gathering */}
                    <div className="legal-disclaimer-box">
                        <h3>Magic: The Gathering</h3>
                        <p>
                            Trade Spot is unofficial Fan Content permitted under the{' '}
                            <a href="https://company.wizards.com/en/legal/fancontentpolicy" target="_blank" rel="noopener noreferrer">
                                Fan Content Policy
                            </a>. 
                            Not approved/endorsed by Wizards. Portions of the materials used are property of 
                            Wizards of the Coast. ©Wizards of the Coast LLC.
                        </p>
                        <p className="legal-disclaimer-note">
                            Magic: The Gathering, the mana symbols, the tap symbol, and all 
                            Magic-specific terms and card names are trademarks of Wizards of the Coast LLC.
                        </p>
                    </div>

                    {/* Pokémon */}
                    <div className="legal-disclaimer-box">
                        <h3>Pokémon Trading Card Game</h3>
                        <p>
                            Trade Spot is not produced, endorsed, supported, or affiliated with 
                            Nintendo, The Pokémon Company, or any other Pokémon-related entities.
                        </p>
                        <p className="legal-disclaimer-note">
                            Pokémon and Pokémon character names are trademarks of Nintendo, 
                            Creatures Inc., and GAME FREAK Inc. ©2024 Pokémon. ©1995-2024 
                            Nintendo/Creatures Inc./GAME FREAK Inc.
                        </p>
                    </div>

                    {/* Placeholder for future games */}
                    {/* 
                    <div className="legal-disclaimer-box">
                        <h3>Yu-Gi-Oh!</h3>
                        <p>
                            [Disclaimer for Yu-Gi-Oh! content]
                        </p>
                    </div>
                    */}
                </section>

                {/* ==================== DATA SOURCES ==================== */}
                
                <section>
                    <h2>Data Sources & Attribution</h2>
                    
                    <div className="legal-attribution-box">
                        <h3>MTGJSON</h3>
                        <p>
                            Magic: The Gathering card data is provided by{' '}
                            <a href="https://mtgjson.com" target="_blank" rel="noopener noreferrer">MTGJSON</a>, 
                            an open-source project licensed under the{' '}
                            <a href="https://mtgjson.com/license/" target="_blank" rel="noopener noreferrer">MIT License</a>.
                        </p>
                        <p className="legal-attribution-copyright">
                            Copyright © 2018 – Present, Zach Halpern, Eric Lakatos
                        </p>
                    </div>

                    <div className="legal-attribution-box">
                        <h3>Scryfall</h3>
                        <p>
                            Magic: The Gathering card images are provided by{' '}
                            <a href="https://scryfall.com" target="_blank" rel="noopener noreferrer">Scryfall</a>.
                        </p>
                        <p className="legal-attribution-note">
                            Scryfall is not produced by or endorsed by Wizards of the Coast.
                        </p>
                    </div>

                    <div className="legal-attribution-box">
                        <h3>Pokémon TCG API</h3>
                        <p>
                            Pokémon card data and images are provided by the{' '}
                            <a href="https://pokemontcg.io" target="_blank" rel="noopener noreferrer">Pokémon TCG API</a>.
                        </p>
                    </div>

                    <div className="legal-attribution-box">
                        <h3>Price Data</h3>
                        <p>
                            Market price estimates are derived from data published by{' '}
                            <a href="https://www.tcgplayer.com" target="_blank" rel="noopener noreferrer">TCGplayer</a> and{' '}
                            <a href="https://www.cardkingdom.com" target="_blank" rel="noopener noreferrer">Card Kingdom</a>,
                            via MTGJSON. Prices are estimates for reference only, may lag the market, and are
                            not offers to buy or sell. Trade Spot is not affiliated with either retailer.
                        </p>
                    </div>

                    <div className="legal-attribution-box">
                        <h3>Maps & Geocoding</h3>
                        <p>
                            Maps are rendered with{' '}
                            <a href="https://leafletjs.com" target="_blank" rel="noopener noreferrer">Leaflet</a> using
                            tile imagery and address data ©{' '}
                            <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap contributors</a>,
                            available under the Open Database License. Address verification uses the{' '}
                            <a href="https://nominatim.org" target="_blank" rel="noopener noreferrer">Nominatim</a> geocoding service.
                        </p>
                    </div>

                    {/* Placeholder for future data sources */}
                    {/*
                    <div className="legal-attribution-box">
                        <h3>YGOPRODeck</h3>
                        <p>
                            Yu-Gi-Oh! card data provided by YGOPRODeck API.
                        </p>
                    </div>
                    */}
                </section>

                {/* ==================== GENERAL NOTICES ==================== */}
                
                <section>
                    <h2>General Notices</h2>
                    
                    <h3>Trademark Notice</h3>
                    <p>
                        All product names, logos, and brands are property of their respective owners. 
                        All company, product, and service names used on this website are for identification 
                        purposes only. Use of these names, logos, and brands does not imply endorsement.
                    </p>

                    <h3>Fan Content</h3>
                    <p>
                        Trade Spot is a fan-made, community-driven platform. We are not affiliated with, 
                        endorsed by, or sponsored by any of the game publishers whose products are featured 
                        on this platform.
                    </p>

                    <h3>Image Usage</h3>
                    <p>
                        Card images displayed on Trade Spot are used for identification and reference purposes 
                        in accordance with the respective fan content and fair use policies. We do not claim 
                        ownership of any card artwork or imagery.
                    </p>
                </section>

                {/* ==================== CONTACT ==================== */}
                
                <section>
                    <h2>Questions or Concerns</h2>
                    <p>
                        If you have any questions about our use of third-party content, or if you believe 
                        we have inadvertently violated any policies, please contact us at{' '}
                        <a href="mailto:support@thetraderspot.com">support@thetraderspot.com</a>.
                    </p>
                </section>

                <section className="legal-acknowledgment">
                    <p>
                        <em>
                            We are grateful to the creators and maintainers of the open-source projects 
                            and APIs that make Trade Spot possible.
                        </em>
                    </p>
                </section>
            </div>
        </div>
    );
}

export default CreditsLegal;

